import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from './Layout';
import { campaignsAPI, campaignProvisionsAPI, quarterlyProvisionsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Banknote,
  Calculator,
  FileText,
  Calendar,
  Clock,
  Loader2,
  ChevronRight,
  Users,
  TrendingUp,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import ProvisionManagerExtended from './ProvisionManagerExtended';
import GehaltsrechnerTrainee from './GehaltsrechnerTrainee';
import DienstplanGenerator from './DienstplanGenerator';
import TagesplanungView from './TagesplanungView';
import TimelineDashboard from './TimelineDashboard';

// Helper to get week number from date
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value || 0);
};

const TrainerDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('provision');
  const [loading, setLoading] = useState(true);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [currentProvision, setCurrentProvision] = useState(null);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [myProvisions, setMyProvisions] = useState([]);
  const [myQuarterly, setMyQuarterly] = useState({});
  const [overviewYear, setOverviewYear] = useState(null);
  const [selectedYear] = useState(new Date().getFullYear().toString());
  
  // Modal states for full views
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [gehaltsrechnerOpen, setGehaltsrechnerOpen] = useState(false);
  const [dienstplanOpen, setDienstplanOpen] = useState(false);
  const [tagesplanungOpen, setTagesplanungOpen] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    loadCurrentCampaign();
  }, []);

  const loadCurrentCampaign = async () => {
    setLoading(true);
    try {
      // Get current week
      const currentWeek = getWeekNumber(new Date());
      const currentYear = new Date().getFullYear();
      
      // Load all campaigns for this year
      const campaigns = await campaignsAPI.getAll(currentYear);
      setAllCampaigns(campaigns);
      
      // Find the current or most recent campaign
      let activeCampaign = null;
      
      // First try to find a campaign that includes the current week
      for (const campaign of campaigns) {
        const weeks = campaign.calendar_weeks || [];
        if (weeks.length >= 2) {
          const startWeek = Math.min(...weeks);
          const endWeek = Math.max(...weeks);
          if (currentWeek >= startWeek && currentWeek <= endWeek) {
            activeCampaign = campaign;
            break;
          }
        }
      }
      
      // If no current campaign, find the next upcoming or most recent past campaign
      if (!activeCampaign && campaigns.length > 0) {
        // Sort by start week
        const sortedCampaigns = [...campaigns].sort((a, b) => {
          const aStart = Math.min(...(a.calendar_weeks || [0]));
          const bStart = Math.min(...(b.calendar_weeks || [0]));
          return bStart - aStart; // Most recent first
        });
        
        // Find upcoming or most recent
        activeCampaign = sortedCampaigns.find(c => {
          const startWeek = Math.min(...(c.calendar_weeks || [0]));
          return startWeek >= currentWeek;
        }) || sortedCampaigns[0];
      }
      
      setCurrentCampaign(activeCampaign);
      
      // Load provision for this campaign
      if (activeCampaign && user?.id) {
        try {
          const provision = await campaignProvisionsAPI.get(user.id, activeCampaign.id);
          setCurrentProvision(provision);
        } catch (e) {
          // No provision yet for this campaign
          setCurrentProvision(null);
        }
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
    }
    
    // Load own provision overview (all campaigns + quarterly)
    try {
      const provs = await campaignProvisionsAPI.getMineAll();
      const withData = provs.filter(p => (p.trainees || []).length > 0 || (p.total_provision_brutto || 0) > 0);
      withData.sort((a, b) => String(b.campaign_id).localeCompare(String(a.campaign_id)));
      setMyProvisions(withData);
      
      const years = [...new Set(withData.map(p => parseInt(String(p.campaign_id).split('-')[0])).filter(y => !isNaN(y)))];
      if (years.length === 0) years.push(new Date().getFullYear());
      const quarterly = {};
      await Promise.all(years.map(async (y) => {
        await Promise.all([1, 2, 3, 4].map(async (q) => {
          try {
            const qp = await quarterlyProvisionsAPI.getMine(y, q);
            if (qp && ((qp.trainees?.length || 0) > 0 || qp.total_provision_brutto > 0)) {
              quarterly[`${y}-Q${q}`] = {
                year: y,
                quarter: q,
                brutto: qp.total_provision_brutto || 0,
                netto: qp.total_provision_netto || 0,
                trainees: qp.trainees?.length || 0,
                remaining: typeof qp.remaining_amount === 'number' ? qp.remaining_amount : (qp.total_provision_brutto || 0)
              };
            }
          } catch (e) { /* none */ }
        }));
      }));
      setMyQuarterly(quarterly);
    } catch (error) {
      console.error('Error loading provision overview:', error);
    }
    setLoading(false);
  };

  const getKwLabel = (campaignId) => {
    // Echte KWs aus der Kampagne (Zeitstrahl-Quelle) bevorzugen, sonst aus der ID ableiten
    const camp = allCampaigns.find(c => c.id === campaignId);
    if (camp?.calendar_weeks?.length >= 2) {
      return `KW ${camp.calendar_weeks[0]}-${camp.calendar_weeks[camp.calendar_weeks.length - 1]}`;
    }
    const m = String(campaignId).match(/kw-?(\d+)-(\d+)/i);
    return m ? `KW ${m[1]}-${m[2]}` : campaignId;
  };

  const getStatus = (p) => {
    const total = p.total_provision_brutto || 0;
    const remaining = typeof p.remaining_amount === 'number'
      ? p.remaining_amount
      : total - (p.total_payouts || 0) - (p.total_advances || 0);
    return { total, remaining, isPaid: remaining <= 0 && total > 0 };
  };

  const selectCampaignFromOverview = async (p) => {
    const camp = allCampaigns.find(c => c.id === p.campaign_id)
      || { id: p.campaign_id, name: `${getKwLabel(p.campaign_id)} / ${String(p.campaign_id).split('-')[0]}`, calendar_weeks: [] };
    setCurrentCampaign(camp);
    try {
      const provision = await campaignProvisionsAPI.get(user.id, p.campaign_id);
      setCurrentProvision(provision);
    } catch (e) {
      setCurrentProvision(null);
    }
    setProvisionModalOpen(true);
  };

  // Jahres-Umschalter für die Gesamtübersicht
  const provYear = (cid) => parseInt(String(cid).split('-')[0]);
  const dataYears = [...new Set([
    ...myProvisions.map(p => provYear(p.campaign_id)),
    ...Object.values(myQuarterly).map(q => q.year)
  ])].filter(y => !isNaN(y));
  const availableYears = [...new Set([...dataYears, new Date().getFullYear()])].sort();
  const defaultOverviewYear = dataYears.length ? Math.max(...dataYears) : new Date().getFullYear();
  const activeOverviewYear = overviewYear && availableYears.includes(overviewYear)
    ? overviewYear
    : defaultOverviewYear;
  const visibleProvisions = myProvisions.filter(p => provYear(p.campaign_id) === activeOverviewYear);
  const visibleQuarterly = Object.values(myQuarterly).filter(q => q.year === activeOverviewYear);
  const overviewTotalBrutto = visibleProvisions.reduce((s, p) => s + (p.total_provision_brutto || 0), 0)
    + visibleQuarterly.reduce((s, v) => s + v.brutto, 0);
  const overviewTotalNetto = visibleProvisions.reduce((s, p) => s + (p.total_provision_netto || 0), 0)
    + visibleQuarterly.reduce((s, v) => s + v.netto, 0);

  // If showing full timeline, render it
  if (showTimeline) {
    return (
      <div>
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#1c2e52] to-[#3e4350] p-3">
          <Button
            variant="ghost"
            onClick={() => setShowTimeline(false)}
            className="text-white hover:bg-white/20"
          >
            <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
            Zurück zum Dashboard
          </Button>
        </div>
        <div className="pt-14">
          <TimelineDashboard />
        </div>
      </div>
    );
  }

  const getCampaignWeeks = () => {
    if (!currentCampaign?.calendar_weeks?.length) return 'Keine KW';
    const weeks = currentCampaign.calendar_weeks;
    return `KW ${Math.min(...weeks)}-${Math.max(...weeks)}`;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1c2e52] to-[#3e4350] text-white p-4 sm:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Hallo, {user?.name?.split(' ')[0]}!</h1>
                <p className="text-sm text-[#e6d7bf]">Trainer Dashboard</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex rounded-lg overflow-hidden border border-white/20">
                  {availableYears.map(y => (
                    <button
                      key={y}
                      onClick={() => setOverviewYear(y)}
                      data-testid={`header-year-${y}`}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                        y === activeOverviewYear
                          ? 'bg-white text-[#1c2e52]'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {y}
                    </button>
                  ))}
                </div>
                <Badge className="bg-white/20 text-white hover:bg-white/20 text-sm" data-testid="header-totals-badge">
                  <TrendingUp className="w-3.5 h-3.5 mr-1" />
                  Netto: {formatCurrency(overviewTotalNetto)} · Brutto: {formatCurrency(overviewTotalBrutto)}
                </Badge>
              </div>
            </div>
            
            {/* Navigation Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-5 gap-1 bg-white/10 p-1 rounded-lg">
                <TabsTrigger 
                  value="provision" 
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Banknote className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Provision</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="gehaltsrechner" 
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Calculator className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Rechner</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="wochenplan" 
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Wochenplan</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="tagesplanung" 
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Calendar className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Tagesplan</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="zeitstrahl" 
                  className="data-[state=active]:bg-white data-[state=active]:text-[#1c2e52] text-white text-xs sm:text-sm"
                >
                  <Clock className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Zeitstrahl</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-6xl mx-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#4b95d1]" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* Provision Tab */}
              <TabsContent value="provision" className="mt-0">
                <div className="space-y-4">
                  {/* Eigene Karte im Admin-Stil (nur eigene Daten) */}
                  <Card className="border-l-4" style={{ borderLeftColor: '#4b95d1' }} data-testid="trainer-overview-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                            style={{ backgroundColor: '#4b95d1' }}
                          >
                            {user?.name?.charAt(0) || '?'}
                          </div>
                          {user?.name}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            Netto: {formatCurrency(overviewTotalNetto)} · Brutto: {formatCurrency(overviewTotalBrutto)}
                          </Badge>
                          {currentCampaign && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setProvisionModalOpen(true)}
                              data-testid="trainer-overview-details-btn"
                            >
                              Details
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {visibleProvisions.length === 0 && visibleQuarterly.length === 0 ? (
                        <p className="text-sm text-gray-500 py-2">Keine Provisionen in {activeOverviewYear}</p>
                      ) : (
                        <div className="space-y-2">
                          {visibleProvisions.map(p => {
                            const status = getStatus(p);
                            return (
                              <div
                                key={p.campaign_id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => selectCampaignFromOverview(p)}
                                data-testid={`trainer-overview-row-${p.campaign_id}`}
                              >
                                <div className="flex items-center gap-3 flex-wrap">
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {getKwLabel(p.campaign_id)}
                                  </Badge>
                                  <span className="text-sm text-gray-600">
                                    {(p.trainees || []).length} Trainees
                                  </span>
                                  <span className="text-[11px] text-gray-400" data-testid={`overview-factor-${p.campaign_id}`}>
                                    ×{p.multiplier_w12 ?? 1.4} · {((p.mwst_rate ?? 0.19) * 100).toFixed(0)} % USt
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <div className="text-right">
                                    <span className="font-bold text-[#1c2e52] block leading-tight">
                                      {formatCurrency(p.total_provision_brutto)}
                                    </span>
                                    <span className="text-[11px] text-gray-500">
                                      Netto: {formatCurrency(p.total_provision_netto)}
                                    </span>
                                  </div>
                                  {status.total > 0 && (
                                    status.isPaid ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Ausgezahlt
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Offen: {formatCurrency(status.remaining)}
                                      </Badge>
                                    )
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {visibleQuarterly.map(qd => (
                            <div
                              key={`q-${qd.year}-${qd.quarter}`}
                              className="flex items-center justify-between p-3 bg-purple-50 rounded-lg"
                              data-testid={`trainer-quarterly-row-${qd.year}-${qd.quarter}`}
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                <Badge className="bg-purple-500 text-white hover:bg-purple-500 text-xs">
                                  Q{qd.quarter}/{qd.year} - Neuwerber
                                </Badge>
                                <span className="text-sm text-gray-600">{qd.trainees} Neuwerber</span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-right">
                                  <span className="font-bold text-purple-700 block leading-tight">
                                    {formatCurrency(qd.brutto)}
                                  </span>
                                  <span className="text-[11px] text-purple-400">
                                    Netto: {formatCurrency(qd.netto)}
                                  </span>
                                </div>
                                {qd.brutto > 0 && (qd.remaining > 0 ? (
                                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    Offen: {formatCurrency(qd.remaining)}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Ausgezahlt
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                {currentCampaign ? (
                  <div className="space-y-4">
                    {/* Campaign Summary Card */}
                    <Card className="border-l-4 border-l-[#4b95d1]">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Banknote className="w-5 h-5 text-[#4b95d1]" />
                            Aktuelle Kampagne: {currentCampaign.name}
                          </CardTitle>
                          <Button
                            size="sm"
                            onClick={() => setProvisionModalOpen(true)}
                            className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                          >
                            Details & Bearbeiten
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {currentProvision ? (
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500">Trainees</p>
                              <p className="text-2xl font-bold text-[#1c2e52]">
                                {currentProvision.trainees?.length || 0}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500">W1 Einheiten</p>
                              <p className="text-2xl font-bold text-[#1c2e52]">
                                {currentProvision.total_units_week1 || 0}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500">W2 Einheiten</p>
                              <p className="text-2xl font-bold text-[#1c2e52]">
                                {currentProvision.total_units_week2 || 0}
                              </p>
                            </div>
                            <div className="bg-slate-100 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Provision Netto</p>
                              <p className="text-2xl font-bold text-slate-700">
                                {formatCurrency(currentProvision.total_provision_netto)}
                              </p>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-emerald-600">Provision Brutto</p>
                              <p className="text-2xl font-bold text-emerald-600">
                                {formatCurrency(currentProvision.total_provision_brutto)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Noch keine Trainees in dieser Kampagne</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setProvisionModalOpen(true)}
                              className="mt-3"
                            >
                              Trainees hinzufügen
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Trainees List */}
                    {currentProvision?.trainees?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Trainees ({currentProvision.trainees.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {currentProvision.trainees.slice(0, 5).map((trainee, idx) => (
                              <div 
                                key={trainee.id || idx}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-[#4b95d1] flex items-center justify-center text-white text-sm font-bold">
                                    {trainee.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <p className="font-medium">{trainee.name}</p>
                                    <p className="text-xs text-gray-500">
                                      W1: {trainee.units_week1 || 0} | W2: {trainee.units_week2 || 0}
                                    </p>
                                  </div>
                                </div>
                                <Badge variant="outline">
                                  {formatCurrency((trainee.units_week1 || 0) * 8.33 + (trainee.units_week2 || 0) * 8.33)}
                                </Badge>
                              </div>
                            ))}
                            {currentProvision.trainees.length > 5 && (
                              <Button
                                variant="ghost"
                                className="w-full text-[#4b95d1]"
                                onClick={() => setProvisionModalOpen(true)}
                              >
                                +{currentProvision.trainees.length - 5} weitere anzeigen
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-gray-500">
                      <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Keine aktive Kampagne gefunden</p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => setShowTimeline(true)}
                      >
                        Zum Zeitstrahl
                      </Button>
                    </CardContent>
                  </Card>
                )}
                </div>
              </TabsContent>
              
              {/* Gehaltsrechner Tab */}
              <TabsContent value="gehaltsrechner" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-[#4b95d1]" />
                      Trainee Gehaltsrechner
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Berechne das Gehalt für deine Trainees basierend auf Einheiten und Arbeitszeit.
                    </p>
                    <Button
                      onClick={() => setGehaltsrechnerOpen(true)}
                      className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    >
                      <Calculator className="w-4 h-4 mr-2" />
                      Gehaltsrechner öffnen
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Wochenplan Tab */}
              <TabsContent value="wochenplan" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#4b95d1]" />
                      Wochenplan / Dienstplan Generator
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Erstelle einen Wochenplan für dein Team mit Schichten und Arbeitszeiten.
                    </p>
                    <Button
                      onClick={() => setDienstplanOpen(true)}
                      className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Wochenplan erstellen
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Tagesplanung Tab */}
              <TabsContent value="tagesplanung" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                      Tagesplanung - Schulungsprogramm
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      15-Tage Schulungsprogramm mit täglichen Aufgaben, Materialien und Anleitungen.
                    </p>
                    <Button
                      onClick={() => setTagesplanungOpen(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Tagesplanung öffnen
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Zeitstrahl Tab */}
              <TabsContent value="zeitstrahl" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#4b95d1]" />
                      Jahres-Zeitstrahl
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      Übersicht aller Kampagnen im Jahresverlauf mit Kalenderwochen.
                    </p>
                    <Button
                      onClick={() => setShowTimeline(true)}
                      className="bg-[#4b95d1] hover:bg-[#1c2e52]"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Zeitstrahl öffnen
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
        
        {/* Modals */}
        <ProvisionManagerExtended
          open={provisionModalOpen}
          onClose={() => {
            setProvisionModalOpen(false);
            loadCurrentCampaign();
          }}
          readOnly
          lockedTrainer={{ id: user?.id, name: user?.name }}
          initialTrainer={user?.id}
          initialCampaign={currentCampaign?.id}
          initialYear={activeOverviewYear}
        />
        
        <GehaltsrechnerTrainee
          open={gehaltsrechnerOpen}
          onClose={() => setGehaltsrechnerOpen(false)}
        />
        
        <DienstplanGenerator
          open={dienstplanOpen}
          onClose={() => setDienstplanOpen(false)}
        />
        
        <TagesplanungView
          open={tagesplanungOpen}
          onClose={() => setTagesplanungOpen(false)}
        />
      </div>
    </Layout>
  );
};

export default TrainerDashboard;
